import { Button, Loader } from "@mantine/core";
import { IconMailOff, IconMailX, IconUserPlus } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { InviteAcceptance } from "../../components/InviteAcceptance/InviteAcceptance";
import { resolveInviteViewKind } from "../../components/InviteAcceptance/inviteState";
import {
	InviteStatus,
	OrganizationRole,
	useAcceptInviteMutation,
	useInvitePreviewQuery,
} from "../../graphql/generated/types.gen";
import { useActiveOrganization } from "../../hooks/useActiveOrganization";
import { useAuthCompat } from "../../hooks/useAuthCompat";

export const Route = createFileRoute("/invite/$token")({
	component: InviteAcceptPage,
});

function InviteAcceptPage() {
	const { token } = Route.useParams();
	const navigate = useNavigate();
	const { user, isLoading: authLoading, signUp, signOut } = useAuthCompat();
	const { setActiveOrganization, refetch: refetchOrgs } =
		useActiveOrganization();

	const {
		data,
		loading: previewLoading,
		error: previewError,
	} = useInvitePreviewQuery({
		variables: { inviteCode: token },
		fetchPolicy: "network-only",
	});

	const [acceptInvite] = useAcceptInviteMutation();
	const [accepting, setAccepting] = useState(false);
	const [acceptError, setAcceptError] = useState<string | null>(null);
	const attemptedRef = useRef(false);

	const preview = data?.invitePreview;

	const emailMatches =
		!!user?.email &&
		!!preview?.email &&
		user.email.trim().toLowerCase() === preview.email.trim().toLowerCase();

	let effectiveStatus: InviteStatus | null = preview?.status ?? null;
	if (preview?.status === InviteStatus.Pending && preview.isExpired) {
		effectiveStatus = InviteStatus.Expired;
	}

	const kind = resolveInviteViewKind({
		loading: previewLoading || authLoading,
		previewError: !!previewError,
		hasPreview: !!preview,
		status: effectiveStatus,
		isAuthenticated: !!user,
		emailMatches,
	});

	const doAccept = useCallback(async () => {
		attemptedRef.current = true;
		setAccepting(true);
		setAcceptError(null);
		try {
			const res = await acceptInvite({ variables: { inviteCode: token } });
			const result = res.data?.acceptInvite;
			if (result) {
				await refetchOrgs();
				setActiveOrganization(result.organizationId);
			}
			navigate({ to: "/" });
		} catch (err) {
			attemptedRef.current = false;
			setAccepting(false);
			setAcceptError(
				err instanceof Error
					? err.message
					: "Could not accept the invitation. Please try again.",
			);
		}
	}, [acceptInvite, token, refetchOrgs, setActiveOrganization, navigate]);

	// Once we reach the "joining" state (authenticated + email matches), accept
	// automatically. The ref guards against a double-fire.
	useEffect(() => {
		if (kind === "joining" && !attemptedRef.current) {
			void doAccept();
		}
	}, [kind, doAccept]);

	const orgName = preview?.organization?.name ?? "the organization";
	const inviterName = preview?.createdByUser?.displayName ?? "Someone";
	const roleLabel =
		preview?.role === OrganizationRole.Admin ? "an admin" : "a member";

	switch (kind) {
		case "loading":
			return (
				<InviteAcceptance icon={<Loader />} title="Loading your invitation…" />
			);
		case "notFound":
			return (
				<InviteAcceptance
					icon={<IconMailX size={40} />}
					title="Invitation not found"
					description="This invitation link is invalid. Ask an admin to send you a new one."
				/>
			);
		case "revoked":
			return (
				<InviteAcceptance
					icon={<IconMailOff size={40} />}
					title="Invitation revoked"
					description="This invitation is no longer valid. Ask an admin to send you a new one."
				/>
			);
		case "expired":
			return (
				<InviteAcceptance
					icon={<IconMailOff size={40} />}
					title="Invitation expired"
					description={`This invitation to join ${orgName} has expired. Ask an admin to resend it.`}
				/>
			);
		case "accepted":
			return (
				<InviteAcceptance
					title="Invitation already accepted"
					description="This invitation has already been used."
				>
					<Button color="violet" onClick={() => navigate({ to: "/" })}>
						Go to Relatance
					</Button>
				</InviteAcceptance>
			);
		case "signedOut":
			return (
				<InviteAcceptance
					icon={<IconUserPlus size={40} />}
					title={`Join ${orgName} on Relatance`}
					description={`${inviterName} invited you to join ${orgName} as ${roleLabel}. Sign up or sign in with ${preview?.email} to accept.`}
				>
					<Button
						color="violet"
						size="md"
						onClick={() => signUp(`/invite/${token}`)}
					>
						Accept invitation
					</Button>
				</InviteAcceptance>
			);
		case "mismatch":
			return (
				<InviteAcceptance
					icon={<IconMailX size={40} />}
					title="You're signed in with a different account"
					description={`This invitation was sent to ${preview?.email}, but you're signed in as ${user?.email ?? "another account"}. Sign out and sign in with ${preview?.email} to accept.`}
				>
					<Button variant="light" color="violet" onClick={() => signOut()}>
						Sign out
					</Button>
				</InviteAcceptance>
			);
		default:
			return (
				<InviteAcceptance
					icon={accepting ? <Loader /> : <IconUserPlus size={40} />}
					title={`Joining ${orgName}…`}
					description={
						acceptError ?? "Hang tight while we add you to the organization."
					}
				>
					{acceptError && (
						<Button color="violet" onClick={() => void doAccept()}>
							Try again
						</Button>
					)}
				</InviteAcceptance>
			);
	}
}
